import React, { useContext, createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSet } from 'react-use';
import { hex } from 'wcag-contrast';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var ConsentManagerContext = /*#__PURE__*/createContext({
  fallbackComponent: function fallbackComponent() {
    return null;
  },
  config: {
    integrations: []
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  store: [{
    decisions: {}
  }, function () {}]
});
function useConfig() {
  var _useContext = useContext(ConsentManagerContext),
    config = _useContext.config;
  return config;
}
function useStore() {
  var _useContext2 = useContext(ConsentManagerContext),
    store = _useContext2.store;
  return store;
}
function useIntegrations() {
  var _useContext3 = useContext(ConsentManagerContext),
    config = _useContext3.config;
  return config.integrations;
}

function initializeDecisionsFromStorage(configuredIntegrations, storage) {
  var _store$decisions;
  var store = storage[0];
  var storedDecisions = (_store$decisions = store == null ? void 0 : store.decisions) != null ? _store$decisions : {};
  var decisions = {};
  var pending = [];
  for (var _iterator = _createForOfIteratorHelperLoose(configuredIntegrations), _step; !(_step = _iterator()).done;) {
    var integration = _step.value;
    var userDecision = storedDecisions[integration.id];
    var enabledByDefault = userDecision === undefined && integration.enabledByDefault;
    var storedDecision = userDecision || enabledByDefault;
    if (typeof storedDecision === 'undefined') {
      pending.push(integration.id);
    }
    decisions[integration.id] = Boolean(storedDecision);
  }
  return {
    decisions: decisions,
    pending: pending
  };
}
function useCombinedIntegrationStoreDecisions() {
  var storage = useStore();
  var integrations = useIntegrations();
  var result = useMemo(function () {
    return initializeDecisionsFromStorage(integrations, storage);
  }, [integrations, storage]);
  return result;
}
function useDecisions() {
  var _useContext = useContext(ConsentManagerContext),
    onChangeDecision = _useContext.config.onChangeDecision;
  var _useCombinedIntegrati = useCombinedIntegrationStoreDecisions(),
    decisions = _useCombinedIntegrati.decisions;
  var _useState = useState(decisions),
    decisionsState = _useState[0],
    setDecisions = _useState[1];
  var _useStore = useStore(),
    setStore = _useStore[1];
  useEffect(function () {
    if (decisionsState === decisions) {
      return;
    }
    // TODO: spread old here to preserve decisions
    setDecisions(function () {
      return decisions;
    });
  }, [decisions, decisionsState, setDecisions, setStore]);
  var setAndStoreDecisions = useCallback(function (newDecisionState) {
    var nextDecisionState = typeof newDecisionState === 'function' ? newDecisionState(decisionsState) : newDecisionState;
    if (onChangeDecision) {
      onChangeDecision(decisionsState, nextDecisionState);
    }
    setStore(function (store) {
      return _extends({}, store, {
        decisions: nextDecisionState
      });
    });
  }, [decisionsState, onChangeDecision, setStore]);
  return [decisionsState, setAndStoreDecisions];
}
function usePendingDecisions() {
  var _useCombinedIntegrati2 = useCombinedIntegrationStoreDecisions(),
    pending = _useCombinedIntegrati2.pending;
  return pending != null ? pending : [];
}
function useConsentFormVisible() {
  var pending = usePendingDecisions();
  return pending.length !== 0;
}

var IntegrationWrapperComponents = function IntegrationWrapperComponents(_ref) {
  var config = _ref.config,
    children = _ref.children;
  var _useDecisions = useDecisions(),
    decisions = _useDecisions[0];
  var _useState = useState(false),
    isMounted = _useState[0],
    setIsMounted = _useState[1];
  useEffect(function () {
    return setIsMounted(true);
  }, [setIsMounted]);
  var wrapperComponents = useMemo(function () {
    if (!isMounted) {
      return null;
    }
    return config.integrations.filter(function (i) {
      return decisions[i.id] === true;
    }).filter(function (i) {
      return Boolean(i.WrapperComponent);
    }).map(function (_ref2) {
      var WrapperComponent = _ref2.WrapperComponent,
        id = _ref2.id;
      return React.createElement(WrapperComponent, {
        key: id
      });
    });
  }, [config, decisions, isMounted]);
  return React.createElement(React.Fragment, null, children, wrapperComponents);
};

var _excluded = ["integrationId", "fallbackUrl"];
var FallbackComponent = function FallbackComponent(_ref) {
  var integrationId = _ref.integrationId,
    fallbackUrl = _ref.fallbackUrl,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  var integration = useIntegration(integrationId);
  var _useDecision = useDecision(integrationId),
    setDecision = _useDecision[1];
  var enableIntegration = useCallback(function () {
    setDecision(true);
  }, [setDecision]);
  if (!integration) {
    throw new Error("No integration found for \"" + integrationId + "\"");
  }
  var title = integration.title,
    privacyPolicyUrl = integration.privacyPolicyUrl,
    Icon = integration.Icon,
    description = integration.description,
    color = integration.color;
  return React.createElement("section", Object.assign({
    style: {
      backgroundColor: '#ddd',
      padding: '1rem 2rem',
      border: '4px solid #222'
    }
  }, props), React.createElement("h1", {
    style: {
      display: 'flex',
      alignItems: 'center'
    }
  }, Icon && React.createElement(Icon, {
    "aria-hidden": "true",
    color: color,
    style: {
      width: '2rem',
      marginRight: '1rem'
    }
  }), title), description && React.createElement("p", null, description), privacyPolicyUrl && React.createElement("p", null, React.createElement("a", {
    href: privacyPolicyUrl,
    target: "_blank",
    rel: "noreferrer"
  }, "Privacy policy of ", title)), React.createElement("button", {
    onClick: function onClick() {
      return enableIntegration();
    },
    style: {
      display: 'block',
      margin: '0 auto'
    }
  }, "Enable ", integration.title), fallbackUrl && React.createElement("p", null, "Alternative:", React.createElement("br", null), "Visit", ' ', React.createElement("a", {
    href: fallbackUrl,
    target: "_blank",
    rel: "noreferrer"
  }, fallbackUrl)));
};

function useEnabledIntegrations() {
  var integrations = useIntegrations();
  var _useDecisions = useDecisions(),
    decisions = _useDecisions[0],
    setDecisions = _useDecisions[1];
  var enabledIntegrations = useMemo(function () {
    return integrations.filter(function (_ref) {
      var id = _ref.id;
      return Boolean(decisions[id]);
    }).map(function (_ref2) {
      var id = _ref2.id;
      return id;
    });
  }, [integrations, decisions]);
  var setEnabled = useCallback(function (newState) {
    var nextEnabledIntegrations = typeof newState === 'function' ? newState(enabledIntegrations) : newState;
    var decisions = {};
    for (var _iterator = _createForOfIteratorHelperLoose(integrations), _step; !(_step = _iterator()).done;) {
      var integration = _step.value;
      var enabled = nextEnabledIntegrations.includes(integration.id);
      decisions[integration.id] = enabled;
    }
    setDecisions(decisions);
  }, [enabledIntegrations, integrations, setDecisions]);
  return [enabledIntegrations, setEnabled];
}
function useIntegration(id) {
  var integrations = useIntegrations();
  return integrations.find(function (i) {
    return i.id === id;
  });
}

var DecisionsForm = function DecisionsForm(_ref) {
  var integrations = _ref.integrations,
    initialValues = _ref.initialValues,
    onSubmit = _ref.onSubmit;
  var _useSet = useSet(new Set(initialValues.enabled)),
    set = _useSet[0],
    _useSet$ = _useSet[1],
    toggle = _useSet$.toggle,
    has = _useSet$.has;
  var handleChange = useCallback(function (e) {
    var integrationId = e.target.value;
    toggle(integrationId);
  }, [toggle]);
  var handleSubmit = useCallback(function (e) {
    e.preventDefault();
    var enabled = Array.from(set.values());
    onSubmit({
      enabled: enabled
    });
  }, [set, onSubmit]);
  return React.createElement("form", {
    onSubmit: handleSubmit
  }, integrations.map(function (_ref2) {
    var id = _ref2.id,
      title = _ref2.title;
    return React.createElement("label", {
      key: id
    }, React.createElement("input", {
      name: "enabled",
      type: "checkbox",
      value: id,
      onChange: handleChange,
      checked: has(id)
    }), ' ', title);
  }), React.createElement("button", {
    type: "submit"
  }, "Submit"));
};

var _excluded$1 = ["formComponent"];
var ConsentManagerForm = function ConsentManagerForm(_ref) {
  var formComponent = _ref.formComponent,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var _useEnabledIntegratio = useEnabledIntegrations(),
    enabledIntegrations = _useEnabledIntegratio[0],
    setEnabledIntegrations = _useEnabledIntegratio[1];
  var _useConfig = useConfig(),
    integrations = _useConfig.integrations;
  var DecisionsForm$1 = formComponent || DecisionsForm;
  var handleFormSubmit = useCallback(function (values) {
    setEnabledIntegrations(function () {
      return values.enabled;
    });
  }, [setEnabledIntegrations]);
  return React.createElement(DecisionsForm$1, Object.assign({
    integrations: integrations,
    initialValues: {
      enabled: enabledIntegrations
    },
    onSubmit: handleFormSubmit
  }, props));
};

var _excluded$2 = ["color"];
function getForegroundColor(bgHex) {
  return hex(bgHex, '#000') >= 7 ? '#000' : '#fff';
}
var createIconComponentFromSimpleIconsSvgPath = function createIconComponentFromSimpleIconsSvgPath(title, path) {
  return function (_ref) {
    var _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'currentColor' : _ref$color,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$2);
    return React.createElement("svg", Object.assign({
      xmlns: "http://www.w3.org/2000/svg",
      fill: color,
      viewBox: "0 0 24 24",
      /* Ensure SVG behaves responsive on Safari and some older browsers like IE */
      height: "100%",
      width: "100%"
    }, props), React.createElement("title", null, title), React.createElement("path", {
      d: path
    }));
  };
};

var _excluded$3 = ["id", "children"];
var ConsentManager = function ConsentManager(_ref) {
  var children = _ref.children,
    config = _ref.config,
    _ref$fallbackComponen = _ref.fallbackComponent,
    fallbackComponent = _ref$fallbackComponen === void 0 ? FallbackComponent : _ref$fallbackComponen,
    store = _ref.store;
  return React.createElement(ConsentManagerContext.Provider, {
    value: {
      fallbackComponent: fallbackComponent,
      config: config,
      store: store
    }
  }, React.createElement(IntegrationWrapperComponents, {
    config: config
  }, children));
};
function useDecision(id) {
  var _decisions$id;
  var _useDecisions = useDecisions(),
    decisions = _useDecisions[0],
    setAndStoreDecisions = _useDecisions[1];
  var decision = (_decisions$id = decisions[id]) != null ? _decisions$id : false;
  return [decision, function (value) {
    var newStateValue = typeof value === 'function' ? value(decision) : value;
    setAndStoreDecisions(function (decisions) {
      var _extends2;
      return _extends({}, decisions, (_extends2 = {}, _extends2[id] = newStateValue, _extends2));
    });
  }];
}
function useFallbackComponent() {
  var _useContext = useContext(ConsentManagerContext),
    FallbackComponent = _useContext.fallbackComponent;
  if (!FallbackComponent) {
    return function () {
      return null;
    };
  }
  return FallbackComponent;
}
var PrivacyShield = function PrivacyShield(_ref2) {
  var id = _ref2.id,
    children = _ref2.children,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded$3);
  var _useState = useState(true),
    isSSR = _useState[0],
    setIsSSR = _useState[1];
  var _useDecision = useDecision(id),
    decision = _useDecision[0];
  var DefaultFallbackComponent = useFallbackComponent();
  useEffect(function () {
    return setIsSSR(false);
  });
  if (!isSSR && decision) {
    return React.createElement(React.Fragment, null, children);
  }
  return React.createElement(DefaultFallbackComponent, Object.assign({
    integrationId: id
  }, props));
};
function usePageViewEventTrigger(id) {
  var _useDecision2 = useDecision(id),
    decision = _useDecision2[0];
  var _useContext2 = useContext(ConsentManagerContext),
    config = _useContext2.config;
  var integration = config.integrations.find(function (i) {
    return i.id === id;
  });
  if (!decision || !integration || typeof integration.pageViewEventHandler !== 'function') {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
  }
  return integration.pageViewEventHandler;
}

export { ConsentManager, ConsentManagerForm, PrivacyShield, createIconComponentFromSimpleIconsSvgPath, getForegroundColor, useConsentFormVisible, useDecision, useDecisions, useEnabledIntegrations, useFallbackComponent, useIntegration, useIntegrations, usePageViewEventTrigger };
//# sourceMappingURL=core.esm.js.map
